import React, { useMemo } from 'react';
import type { CompanyCustomField } from '../../services/companyCustomizationApi';
import { useI18n } from '../../contexts/I18nContext';

type CustomFieldsFormProps = {
  fields: CompanyCustomField[];
  values: Record<string, unknown>;
  errors?: Record<string, string>;
  onChange: (next: Record<string, unknown>) => void;
  isLoading?: boolean;
  emptyStateMessage?: string;
};

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '6px',
  fontSize: '14px',
  fontWeight: 500,
};

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  fontSize: '12px',
  marginTop: '4px',
};

const helperStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  marginTop: '4px',
};

const getFieldValue = (values: Record<string, unknown>, fieldKey: string, defaultValue: unknown = '') => {
  const { t } = useI18n();
  if (values[fieldKey] === undefined || values[fieldKey] === null) {
    return defaultValue;
  }
  return values[fieldKey];
};

const normalizeBoolean = (value: unknown) => Boolean(value);

const deserializeMultiValue = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
  return [];
};

const CustomFieldsForm: React.FC<CustomFieldsFormProps> = ({
  fields,
  values,
  errors,
  onChange,
  isLoading,
  emptyStateMessage = 'Nenhum campo personalizado configurado para este formulário.',
}) => {
  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.position - b.position || a.fieldKey.localeCompare(b.fieldKey)),
    [fields],
  );

  const handleValueChange = (fieldKey: string, value: unknown) => {
    onChange({
      ...values,
      [fieldKey]: value,
    });
  };

  if (isLoading) {
    return <div style={{ padding: '12px', color: '#6b7280' }}>{t('customFields.loading')}</div>;
  }

  if (sortedFields.length === 0) {
    return (
      <div
        style={{
          padding: '16px',
          borderRadius: '6px',
          border: '1px dashed #d1d5db',
          color: '#6b7280',
          fontSize: '14px',
        }}
      >
        {emptyStateMessage}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {sortedFields.map((field) => {
        const error = errors?.[field.fieldKey];
        const helperText = typeof field.config?.helper_text === 'string' ? field.config.helper_text : undefined;
        const placeholder =
          typeof field.config?.placeholder === 'string' ? field.config.placeholder : `Digite ${field.label}`;

        const fieldId = `custom-field-${field.module}-${field.fieldKey}`;

        const renderInput = () => {
          switch (field.fieldType) {
            case 'textarea':
              return (
                <textarea
                  id={fieldId}
                  name={field.fieldKey}
                  value={String(getFieldValue(values, field.fieldKey, ''))}
                  onChange={(event) => handleValueChange(field.fieldKey, event.target.value)}
                  rows={field.config?.rows ? Number(field.config.rows) : 4}
                  placeholder={placeholder}
                  style={{ ...inputBaseStyle, minHeight: 80, resize: 'vertical' }}
                />
              );
            case 'number':
              return (
                <input
                  id={fieldId}
                  name={field.fieldKey}
                  type="number"
                  value={String(getFieldValue(values, field.fieldKey, ''))}
                  onChange={(event) => handleValueChange(field.fieldKey, event.target.value)}
                  placeholder={placeholder}
                  style={inputBaseStyle}
                />
              );
            case 'select':
              return (
                <select
                  id={fieldId}
                  name={field.fieldKey}
                  value={String(getFieldValue(values, field.fieldKey, ''))}
                  onChange={(event) => handleValueChange(field.fieldKey, event.target.value)}
                  style={inputBaseStyle}
                >
                  <option value="">{field.config?.placeholder ?? 'Selecione uma opção'}</option>
                  {(field.config?.options ?? []).map((option: unknown) => {
                    const label = typeof option === 'string' ? option : JSON.stringify(option);
                    const value = typeof option === 'string' ? option : option?.value ?? label;
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              );
            case 'multiselect': {
              const selected = deserializeMultiValue(getFieldValue(values, field.fieldKey));
              return (
                <select
                  id={fieldId}
                  name={field.fieldKey}
                  multiple
                  value={selected}
                  onChange={(event) => {
                    const options = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                    handleValueChange(field.fieldKey, options);
                  }}
                  style={{ ...inputBaseStyle, height: 120 }}
                >
                  {(field.config?.options ?? []).map((option: any) => {
                    const label = typeof option === 'string' ? option : option?.label ?? option?.value;
                    const value = typeof option === 'string' ? option : option?.value ?? label;
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              );
            }
            case 'checkbox': {
              const checked = normalizeBoolean(getFieldValue(values, field.fieldKey, false));
              return (
                <label htmlFor={fieldId} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    id={fieldId}
                    name={field.fieldKey}
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => handleValueChange(field.fieldKey, event.target.checked)}
                  />
                  <span>{field.config?.checkbox_label || field.label}</span>
                </label>
              );
            }
            case 'date':
            case 'time':
              return (
                <input
                  id={fieldId}
                  name={field.fieldKey}
                  type={field.fieldType}
                  value={String(getFieldValue(values, field.fieldKey, ''))}
                  onChange={(event) => handleValueChange(field.fieldKey, event.target.value)}
                  style={inputBaseStyle}
                />
              );
            default:
              return (
                <input
                  id={fieldId}
                  name={field.fieldKey}
                  type="text"
                  value={String(getFieldValue(values, field.fieldKey, ''))}
                  onChange={(event) => handleValueChange(field.fieldKey, event.target.value)}
                  placeholder={placeholder}
                  style={inputBaseStyle}
                />
              );
          }
        };

        return (
          <div key={`${field.module}-${field.fieldKey}`}>
            <label htmlFor={fieldId} style={labelStyle}>
              <span>
                {field.label}
                {field.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
              </span>
              {field.config?.hint && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{field.config.hint}</span>
              )}
            </label>
            {renderInput()}
            {helperText && <div style={helperStyle}>{helperText}</div>}
            {error && <div style={errorStyle}>{error}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default CustomFieldsForm;

